/* eslint-disable eqeqeq */
/* eslint-disable import/prefer-default-export */

/**
 * Three-Valued Logic (3VL) operations to mimic SQLite's handling of NULLs.
 * In this logic system, any operation involving NULL can result in TRUE, FALSE, or NULL (unknown).
 *
 * References:
 *
 * https://www.sqlite.org/lang_expr.html
 * https://www.sqlite.org/datatype3.html
 *
 */
const TVL = {
  // NOT NULL is NULL.
  not: (a) => (a === null ? null : !a),

  // AND: false wins over null.
  and: (a, b) => {
    if (a === false || b === false) return false;
    if (a === null || b === null) return null;
    return true;
  },

  // OR: true wins over null.
  or: (a, b) => {
    if (a === true || b === true) return true;
    if (a === null || b === null) return null;
    return false;
  },

  // --- Comparison Operators ---
  // Any comparison involving a NULL value results in NULL.
  gt: (a, b) => (a === null || b === null ? null : a > b),
  gte: (a, b) => (a === null || b === null ? null : a >= b),
  lt: (a, b) => (a === null || b === null ? null : a < b),
  lte: (a, b) => (a === null || b === null ? null : a <= b),
  // SQLite does type coercion for equality checks, so we should use Javascript `==` and `!=` here (and for is/is not).
  eq: (a, b) => (a === null || b === null ? null : a == b),
  neq: (a, b) => (a === null || b === null ? null : a != b),
  // The `IS` operator is special: It never returns NULL itself (only way to test for NULL)
  is: (a, b) => a == b,
  isNot: (a, b) => a != b,

  // --- Set and Arithmetic Operators ---
  // 'IN' operator: `val IN list`
  in: (val, list) => {
    // `NULL IN (...)` is always NULL.
    if (val === null) return null;
    const flatList = [].concat(list).flat();
    // If a direct match is found, it's TRUE.
    if (flatList.some((item) => item === val)) return true;
    // If no match is found, but the list contains a NULL, the result is "unknown" (NULL).
    if (flatList.some((item) => item === null)) return null;
    // Otherwise, it's definitively FALSE.
    return false;
  },

  // Any arithmetic operation involving a NULL value results in NULL.
  add: (a, b) => (a === null || b === null ? null : a + b),
  sub: (a, b) => (a === null || b === null ? null : a - b),
  mul: (a, b) => (a === null || b === null ? null : a * b),
  div: (a, b) => (a === null || b === null || b === 0 ? null : a / b), // SQLite returns NULL on division by zero.
  mod: (a, b) => (a === null || b === null ? null : a % b),
};

export { TVL };
